/**
 * One column of a column browser (as in a file manager): a titled list of
 * radios that scrolls on its own, so picking along a path (connection →
 * project → agent → version) keeps every choice in view instead of stacking
 * them down the page. A filter box shows once the list is long.
 */

import { Search } from "lucide-react";
import { useState, type ReactNode } from "react";

export interface ColumnItem {
  value: string;
  label: string;
  /** A second line, e.g. a base URL or a description. */
  note?: string | undefined;
  /** Shown after the label, e.g. a pill. */
  extra?: ReactNode;
  mono?: boolean | undefined;
}

/** More items than this and a filter box shows. */
const FILTER_FROM = 7;

export function ColumnList({
  title,
  name,
  items,
  value,
  onChange,
  disabled = false,
  waiting,
}: {
  title: string;
  /** The radios' group name; also their fieldset's accessible name is `title`. */
  name: string;
  items: ColumnItem[] | undefined;
  value: string | null;
  onChange: (value: string) => void;
  disabled?: boolean;
  /** Shown instead of the list: why there is nothing to pick yet ("Loading…", "Pick a project first"). */
  waiting?: string;
}) {
  const [query, setQuery] = useState("");
  const all = items ?? [];
  const q = query.trim().toLowerCase();
  const shown = q ? all.filter((i) => `${i.label} ${i.note ?? ""}`.toLowerCase().includes(q)) : all;

  return (
    <fieldset aria-label={title} disabled={disabled} className="flex min-h-0 min-w-0 flex-col">
      <div className="flex h-9 shrink-0 items-center gap-2 border-b border-line-soft px-3">
        <span className="text-[11px] font-semibold tracking-[0.05em] text-faint uppercase">{title}</span>
        {all.length > 0 && <span className="text-[11px] text-dim tabular-nums">{all.length}</span>}
      </div>
      {all.length > FILTER_FROM && (
        <label className="relative shrink-0 border-b border-line-soft">
          <span className="sr-only">Filter {title.toLowerCase()}</span>
          <Search aria-hidden className="absolute top-1/2 left-3 size-3.5 -translate-y-1/2 text-faint" />
          <input
            className="h-8 w-full bg-transparent pr-3 pl-8 text-xs outline-none placeholder:text-faint"
            value={query}
            placeholder="Filter"
            onChange={(e) => setQuery(e.target.value)}
          />
        </label>
      )}
      <div className="flex max-h-[264px] min-h-[132px] flex-col overflow-y-auto p-1.5">
        {waiting && !all.length ? (
          <p className="m-auto px-3 text-center text-xs text-faint">{waiting}</p>
        ) : (
          shown.map((item) => (
            <Row key={item.value} item={item} name={name} checked={item.value === value} onPick={onChange} />
          ))
        )}
        {q && !shown.length && <p className="m-auto text-xs text-faint">No match</p>}
      </div>
    </fieldset>
  );
}

function Row({
  item,
  name,
  checked,
  onPick,
}: {
  item: ColumnItem;
  name: string;
  checked: boolean;
  onPick: (value: string) => void;
}) {
  return (
    <label
      className={`flex cursor-pointer items-start gap-2.5 rounded-md px-2.5 py-2 transition-colors ${
        checked ? "bg-run-bg/60" : "hover:bg-hover"
      } has-disabled:cursor-not-allowed has-disabled:opacity-60`}
    >
      <input
        type="radio"
        name={name}
        className="mt-0.5 size-3.5 shrink-0 accent-link"
        checked={checked}
        onChange={() => onPick(item.value)}
      />
      <span className="flex min-w-0 flex-1 flex-col gap-0.5">
        <span className="flex min-w-0 items-center gap-1.5">
          <span
            className={`truncate text-[13px] ${checked ? "font-semibold text-ink" : "font-medium text-body"} ${item.mono ? "font-mono" : ""}`}
          >
            {item.label}
          </span>
          {item.extra}
        </span>
        {item.note && <span className="line-clamp-2 text-[11px] leading-snug text-muted">{item.note}</span>}
      </span>
    </label>
  );
}
